import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang, useT } from "../../context/LanguageContext";
import { t } from "../../data/translations";
import { TID } from "../../constants/testIds";

const NavItem = ({ to, label, testid, onClick, twoLine }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      `relative flex items-center justify-center text-center px-2 py-2 text-xs lg:text-sm font-semibold tracking-wide leading-tight transition-colors ${
        twoLine ? "whitespace-normal w-20" : "whitespace-nowrap"
      } ${isActive ? "text-red-700" : "text-slate-700 hover:text-red-700"}`
    }
  >
    {({ isActive }) => (
      <>
        <span>{label}</span>
        <span
          className={`absolute left-2 right-2 -bottom-0.5 h-[2px] rounded-full transition-all ${
            isActive ? "bg-amber-600" : "bg-transparent"
          }`}
        />
      </>
    )}
  </NavLink>
);

const Header = () => {
  const tr = useT();
  const { lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: tr(t.nav.home), tid: TID.navHome },
    { to: "/about", label: tr(t.nav.about), tid: TID.navAbout },
    { to: "/certificate", label: tr(t.nav.documents), tid: TID.navCertificate },
    { to: "/deposits", label: tr(t.nav.deposits), tid: TID.navDeposits, twoLine: true },
    { to: "/loans", label: tr(t.nav.loans), tid: TID.navLoans, twoLine: true },
    { to: "/calculator", label: tr(t.nav.calculator), tid: TID.navCalculator, twoLine: true },
    { to: "/contact", label: tr(t.nav.contact), tid: TID.navContact },
  ];

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200 transition-shadow ${
        scrolled ? "header-scrolled" : ""
      }`}
    >
      {/* Top contact strip */}
      <div className="hidden md:block bg-gradient-to-r from-red-700 via-red-700 to-amber-600 text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2">
              <i className="fa-solid fa-phone text-amber-200" />
              9987070049
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="fa-solid fa-clock text-amber-200" />
              {tr(t.hours.weekdays)}
            </span>
          </div>
          <span className="inline-flex items-center gap-2">
            <i className="fa-solid fa-location-dot text-amber-200" />
            Wagle Estate, Thane – 400 604
          </span>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link
            to="/"
            data-testid={TID.headerLogo}
            className="flex items-center gap-1 group"
          >
            <div className="relative flex items-center justify-center">
              <div className="w-14 h-14 rounded-full overflow-hidden">
                <img
                  src="/logo-mark.png"
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[15px] lg:text-base text-slate-900">
                {lang === "mr" ? "सुखकर्ता पतपेढी" : "Sukhakarta Patsanstha"}
              </div>
              <div className="text-[10px] lg:text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                {lang === "mr"
                  ? "विश्वासाची सहकारी संस्था"
                  : "Cooperative · Since 1996"}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-3">
            {links.map((l) => (
              <NavItem key={l.to} to={l.to} label={l.label} testid={l.tid} twoLine={l.twoLine} />
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              data-testid={TID.langToggle}
              aria-label="Toggle language"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 hover:border-red-700 hover:text-red-700 text-slate-700 text-sm font-semibold transition-colors"
            >
              <i className="fa-solid fa-language" />
              {lang === "mr" ? "English" : "मराठी"}
            </button>
            <Link
              to="/membership"
              data-testid={TID.navMembership}
              className="hidden md:inline-flex items-center gap-1.5 bg-red-700 hover:bg-red-800 text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors shadow-sm"
            >
              {tr(t.cta.joinMember)}
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>

            <button
              onClick={() => setMobileOpen((o) => !o)}
              data-testid={TID.mobileMenuBtn}
              aria-label="Menu"
              className="lg:hidden p-2 text-slate-700 hover:text-red-700"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavItem
                key={l.to}
                to={l.to}
                label={l.label}
                testid={l.tid}
                onClick={() => setMobileOpen(false)}
              />
            ))}
            <Link
              to="/membership"
              data-testid={`${TID.navMembership}-mobile`}
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-1.5 bg-red-700 hover:bg-red-800 text-white font-semibold px-4 py-2.5 rounded-lg text-sm"
            >
              {tr(t.cta.joinMember)}
            </Link>
            <button
              onClick={() => {
                toggle();
                setMobileOpen(false);
              }}
              data-testid={`${TID.langToggle}-mobile`}
              className="mt-1 inline-flex items-center justify-center gap-1.5 border border-slate-300 px-4 py-2 rounded-lg text-sm font-semibold text-slate-700"
            >
              {lang === "mr" ? "Switch to English" : "मराठीत बदला"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
